import React from "react";
import {
  Container,
  Overline,
  Reveal,
  CourseHeader,
  CourseFooter,
  CardShell,
  BulletList,
  useSeo,
  ArrowRight,
  Sparkles,
  CircleDot,
} from "@/components/coursePage/shared";
import {
  Database,
  Filter,
  FileText,
  Scale,
  Flag,
  Layers,
} from "lucide-react";

const EXEC_URL = "https://justin-s-school217.teachable.com/p/ai-executive-workflow-from-data-to-decision-in-15-minutes";

const PROBLEMS = [
  "Scattered information across tools and inboxes",
  "Long reports with buried insights",
  "Competing priorities pulling in different directions",
  "Unclear risks and downstream consequences",
  "Limited decision time",
];

const WORKFLOW = [
  { n: "01", icon: Database, title: "Gather the Information", desc: "Bring together reports, metrics, notes, and relevant context." },
  { n: "02", icon: Filter, title: "Identify the Signal", desc: "Separate important insights from background noise." },
  { n: "03", icon: FileText, title: "Summarize the Situation", desc: "Create a concise executive overview." },
  { n: "04", icon: Scale, title: "Evaluate Options and Risks", desc: "Compare possible actions, tradeoffs, and consequences." },
  { n: "05", icon: Flag, title: "Make the Decision", desc: "Produce a clear recommendation and next-step plan." },
];

const AUDIENCE = [
  "Business owners",
  "Supervisors and managers",
  "Operations leaders",
  "Project managers",
  "Consultants",
  "Entrepreneurs",
  "Professionals responsible for recommendations or decisions",
];

const OUTCOMES = [
  "Analyze business information faster",
  "Create concise executive summaries",
  "Identify key risks and priorities",
  "Compare possible actions",
  "Produce stronger recommendations",
  "Communicate decisions clearly",
];

export default function ExecutiveWorkflowPage() {
  useSeo({
    title: "AI Executive Workflow — From Data to Decision in 15 Minutes | JK Academy",
    description:
      "A practical AI workflow for turning raw information into an executive-ready summary, clear priorities, recommendations, and a confident decision.",
  });

  return (
    <div data-testid="executive-workflow-page" className="min-h-screen bg-[#030712] text-slate-100">
      <CourseHeader enrollUrl={EXEC_URL} enrollLabel="View Course" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-grid opacity-60 animate-grid-pan" />
        <div className="absolute inset-0 -z-10 bg-radial-glow" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-slate-950/70 to-[#030712]" />

        <Container className="py-16 sm:py-24 lg:py-32">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 border border-cyan-500/30 bg-cyan-500/5 px-3 py-1.5 mb-6">
              <CircleDot className="h-3.5 w-3.5 text-cyan-400 animate-pulse" />
              <Overline>JK Academy Executive AI Series</Overline>
            </div>
            <h1
              data-testid="exec-hero-headline"
              className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02]"
            >
              From <span className="text-cyan-400 text-glow-cyan">Data</span> to Decision <br className="hidden sm:block" />
              in <span className="relative inline-block"><span className="relative z-10">15 Minutes</span><span className="absolute -bottom-1 left-0 right-0 h-2 bg-cyan-500/30 -z-0" /></span>.
            </h1>
            <p className="mt-7 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed font-mono">
              Learn a focused AI workflow for turning raw information into an <span className="text-cyan-300">executive-ready summary</span>, clear priorities, practical recommendations, and a confident decision.
            </p>
            <div className="mt-10">
              <a
                href={EXEC_URL}
                rel="noopener noreferrer"
                data-testid="exec-hero-cta"
                className="group inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_48px_rgba(6,182,212,0.75)] transition-all"
              >
                <Sparkles className="h-4 w-4" />
                View the Executive Workflow
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </Container>
      </section>

      {/* Problem */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 items-start">
            <Reveal>
              <Overline>// the problem</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
                More Data Does Not Always Create <span className="text-cyan-400">Better Decisions</span>.
              </h2>
              <p className="mt-5 font-mono text-sm text-slate-400 leading-relaxed max-w-md">
                Leaders spend hours in reports and dashboards, and still walk into the meeting unsure. This course teaches the workflow that closes that gap.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <CardShell>
                <BulletList items={PROBLEMS} testIdPrefix="exec-problem" />
              </CardShell>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Workflow */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <Reveal>
            <Overline>// the workflow</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl">
              Five steps from <span className="text-cyan-400">raw input</span> to <span className="text-cyan-400">clear decision</span>.
            </h2>
          </Reveal>

          <div
            data-testid="exec-workflow-steps"
            className="mt-14 relative"
          >
            <div className="hidden lg:block absolute top-14 left-[6%] right-[6%] h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {WORKFLOW.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.n} delay={i * 0.08}>
                    <div data-testid={`exec-workflow-step-${i + 1}`} className="relative">
                      <div className="relative mx-auto lg:mx-0 h-14 w-14 flex items-center justify-center border border-cyan-500/50 bg-slate-950 text-cyan-300 z-10">
                        <Icon className="h-5 w-5" />
                        <div className="absolute inset-0 -z-10 bg-cyan-500/10 blur-md" />
                      </div>
                      <div className="mt-4 text-center lg:text-left">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
                          {s.n}
                        </span>
                        <h3 className="mt-1 font-display text-lg sm:text-xl font-bold text-white leading-tight">
                          {s.title}
                        </h3>
                        <p className="mt-2 font-mono text-xs text-slate-400 leading-relaxed">
                          {s.desc}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* Audience */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
            <Reveal>
              <Overline>// who this is for</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                Built for people who <span className="text-cyan-400">make the call</span>.
              </h2>
              <p className="mt-5 font-mono text-sm text-slate-400 leading-relaxed max-w-md">
                If you present recommendations or own the outcome, this workflow was built for you.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <CardShell>
                <div className="h-11 w-11 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 mb-5">
                  <Layers className="h-5 w-5" />
                </div>
                <BulletList items={AUDIENCE} testIdPrefix="exec-audience" />
              </CardShell>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Outcomes */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <Reveal>
            <Overline>// what you&apos;ll be able to do</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl">
              After the workflow, you&apos;ll be better prepared to:
            </h2>
          </Reveal>
          <div
            data-testid="exec-outcomes"
            className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
          >
            {OUTCOMES.map((o, i) => (
              <Reveal key={o} delay={i * 0.05}>
                <div className="border border-white/10 bg-slate-900/40 backdrop-blur-sm p-6 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-colors">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
                    Outcome 0{i + 1}
                  </span>
                  <p className="mt-3 font-display text-base sm:text-lg font-semibold text-white leading-snug">
                    {o}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 border-t border-white/5 mb-20 sm:mb-0">
        <Container>
          <Reveal>
            <div className="relative border border-cyan-500/40 bg-slate-900/60 backdrop-blur-md p-10 sm:p-16 max-w-4xl mx-auto text-center shadow-[0_0_80px_rgba(6,182,212,0.2)]">
              <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400" />
              <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400" />
              <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400" />
              <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400" />

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
                Turn <span className="text-cyan-400 text-glow-cyan">Information</span> into Action.
              </h2>
              <p className="mt-5 max-w-xl mx-auto font-mono text-sm sm:text-base text-slate-400 leading-relaxed">
                Learn a repeatable AI workflow that helps you move from scattered data to a clear, confident decision.
              </p>
              <a
                href={EXEC_URL}
                rel="noopener noreferrer"
                data-testid="exec-final-cta"
                className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-10 py-5 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_48px_rgba(6,182,212,0.75)] transition-all"
              >
                <Sparkles className="h-4 w-4" />
                Access the Executive Workflow
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </Container>
      </section>

      <CourseFooter />
    </div>
  );
}
